
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { Search, Dumbbell, ArrowLeft } from "lucide-react";
import ProfileIconLink from "@/components/ProfileIconLink";
import { useEquipment } from "@/hooks/use-equipment";
import { resolveBackendAssetUrl } from "@/integrations/backend/client";
import type { Equipment as EquipmentRow } from "@/integrations/backend/equipment";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type Props = { onBack?: () => void };

const Equipment = ({ onBack }: Props) => {
  const { data, loading, error } = useEquipment();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [brokenImagesById, setBrokenImagesById] = useState<Record<string, true>>({});
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentRow | null>(null);
  const isMobile = useIsMobile();

  const activeEquipment = useMemo(() => data.filter((eq) => !eq.is_deleted), [data]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const eq of activeEquipment) {
      const c = (eq.category || "").trim();
      if (c) cats.add(c);
      else cats.add("Other");
    }
    return ["all", ...Array.from(cats).sort((a, b) => a.localeCompare(b))];
  }, [activeEquipment]);

  const filteredEquipment = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return activeEquipment.filter((eq) => {
      const category = (eq.category || "").trim() || "Other";
      const matchesCategory = selectedCategory === "all" || category === selectedCategory;
      const haystack = `${eq.name} ${category} ${eq.description || ""}`.toLowerCase();
      const matchesSearch = !q || haystack.includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [activeEquipment, searchQuery, selectedCategory]);

  const truncateDescription = (s: string, maxChars: number) => {
    const clean = (s || "").trim();
    if (!clean) return "";
    if (clean.length <= maxChars) return clean;
    return `${clean.slice(0, maxChars).trimEnd()}…`;
  };

  return (
    <div className="min-h-screen gradient-dark">
      
      {/* Header */}
      <div className={`bg-gradient-to-r from-dark-bg to-dark-card text-white p-6 pt-8 pb-10 rounded-b-[2.5rem] border-b-2 border-primary/20 ${!isMobile ? "md:ml-16" : ""}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            {onBack && (
              <Button 
                variant="ghost" 
                className="bg-white/10 rounded-full h-9 px-3 flex items-center justify-center hover:bg-white/20"
                onClick={onBack}
                aria-label="Back to Explore"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span className="text-sm">Back to Explore</span>
              </Button>
            )}
          </div>
          <ProfileIconLink />
        </div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Dumbbell className="w-6 h-6 text-primary" />
              <span>Equipment Hub</span>
            </h1>
            <p className="text-gray-light mt-1">Browse equipment available in your program</p>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary w-5 h-5" />
          <Input
            placeholder="Search equipment..."
            className="pl-12 bg-dark-card border border-primary/10 text-white placeholder:text-gray-light"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className={`p-6 space-y-6 ${isMobile ? "pb-24" : ""} ${!isMobile ? "md:ml-16" : ""}`}>
        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map((category) => (
            <Button
              key={category}
              variant="outline"
              className={`whitespace-nowrap flex items-center gap-2 border-primary/30 ${
                selectedCategory === category 
                  ? "bg-primary text-black border-primary" 
                  : "text-white hover:bg-dark-slate"
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              <span>{category === "all" ? "All Equipment" : category}</span>
            </Button>
          ))}
        </div>

        {/* Equipment Grid */}
        {error ? (
          <Card className="border border-red-500/30 bg-dark-card shadow-card">
            <CardContent className="p-4 text-red-200">
              Failed to load equipment. Please try again.
            </CardContent>
          </Card>
        ) : null}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card
                key={i}
                className="border border-primary/10 bg-dark-card shadow-card overflow-hidden"
              >
                <div className="fh-skeleton h-48 bg-dark-slate border-b border-primary/10" />
                <CardContent className="p-4">
                  <div className="fh-skeleton h-5 w-2/3 bg-dark-slate rounded mb-3" />
                  <div className="fh-skeleton h-4 w-full bg-dark-slate rounded mb-2" />
                  <div className="fh-skeleton h-4 w-5/6 bg-dark-slate rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEquipment.map((eq) => {
            const category = (eq.category || "").trim() || "Other";
            const image = resolveBackendAssetUrl(eq.image_file);
            const showImage = Boolean(image) && !brokenImagesById[eq.id];
            const affiliateUrl = eq.affiliate_url?.trim() || "";
            const truncatedDescription = eq.description ? truncateDescription(eq.description, 100) : "";
            return (
              <Card key={eq.id} className="border border-primary/20 bg-dark-card shadow-card overflow-hidden hover:shadow-neon-glow transition-all">
                <div className="relative">
                  {showImage ? (
                    <img
                      src={image as string}
                      alt={eq.name}
                      className="w-full h-48 object-cover border-b border-primary/20"
                      loading="lazy"
                      onError={() => {
                        setBrokenImagesById((prev) => ({ ...prev, [eq.id]: true }));
                      }}
                    />
                  ) : (
                    <div className="flex h-48 w-full items-center justify-center border-b border-primary/20 bg-dark-slate">
                      <Dumbbell className="h-10 w-10 text-gray-light" aria-hidden="true" />
                      <span className="sr-only">No image available</span>
                    </div>
                  )}
                  <Badge className="absolute top-3 left-3 bg-primary text-black border border-black/20">
                    {category}
                  </Badge>
                </div>

                <CardContent className="p-4 text-white">
                  <h3 className="font-semibold text-lg mb-2">{eq.name}</h3>
                  {truncatedDescription ? (
                    <p className="text-sm text-gray-light mb-4">{truncatedDescription}</p>
                  ) : (
                    <p className="text-sm text-gray-light mb-4">No description yet.</p>
                  )}

                  <div className="mt-2 flex gap-2">
                    <Button
                      variant="outline"
                      className={affiliateUrl ? "flex-1 border-primary/30 text-primary hover:bg-primary hover:text-black" : "w-full border-primary/30 text-primary hover:bg-primary hover:text-black"}
                      onClick={() => {
                        setSelectedEquipment(eq);
                        setDetailsOpen(true);
                      }}
                    >
                      View details
                    </Button>
                    {affiliateUrl ? (
                      <Button asChild className="flex-1">
                        <a href={affiliateUrl} target="_blank" rel="noopener noreferrer">
                          Buy
                        </a>
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {!loading && !error && filteredEquipment.length === 0 ? (
            <div className="col-span-full">
              <div className="rounded-xl border border-dashed border-primary/20 bg-dark-card p-10 text-center text-white">
                <h3 className="text-lg font-semibold">No equipment found</h3>
                <p className="mt-1 text-sm text-gray-light">
                  Try a different category or search term.
                </p>
              </div>
            </div>
          ) : null}
          </div>
        )}
      </div>

      <Dialog
        open={detailsOpen}
        onOpenChange={(open) => {
          setDetailsOpen(open);
          if (!open) setSelectedEquipment(null);
        }}
      >
        <DialogContent className="bg-dark-card text-white border-primary/20">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedEquipment?.name || "Equipment"}</DialogTitle>
            <DialogDescription className="text-gray-light">
              {((selectedEquipment?.category || "").trim() || "Other")}
            </DialogDescription>
          </DialogHeader>

          {selectedEquipment ? (
            <div className="space-y-4">
              {resolveBackendAssetUrl(selectedEquipment.image_file) ? (
                <img
                  src={resolveBackendAssetUrl(selectedEquipment.image_file) as string}
                  alt={selectedEquipment.name}
                  className="w-full max-h-64 object-cover rounded-md border border-primary/20"
                  loading="lazy"
                  onError={() => {
                    setBrokenImagesById((prev) => ({ ...prev, [selectedEquipment.id]: true }));
                  }}
                />
              ) : null}

              <div className="space-y-2">
                <div className="text-sm font-semibold text-white">Description</div>
                <div className="text-sm text-gray-light whitespace-pre-wrap">
                  {selectedEquipment.description?.trim() ? selectedEquipment.description : "No description yet."}
                </div>
              </div>

              {selectedEquipment.instructions?.trim() ? (
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-white">Instructions</div>
                  <div className="text-sm text-gray-light whitespace-pre-wrap">
                    {selectedEquipment.instructions}
                  </div>
                </div>
              ) : null}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className={selectedEquipment.affiliate_url?.trim() ? "flex-1 border-primary/30 text-primary hover:bg-primary hover:text-black" : "w-full border-primary/30 text-primary hover:bg-primary hover:text-black"}
                  onClick={() => setDetailsOpen(false)}
                >
                  Close
                </Button>
                {selectedEquipment.affiliate_url?.trim() ? (
                  <Button asChild className="flex-1">
                    <a
                      href={selectedEquipment.affiliate_url.trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Buy
                    </a>
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Equipment;
